import {
  Alert,
  Container,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useMedplum } from '@medplum/react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { usePermissions } from '@/contexts/PermissionContext';
import { canSelectEnrollmentPractitioner } from '@/lib/enrollmentAccess';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import {
  enrollmentPractitionerOptions,
  fetchEligibleEnrollmentPractitioners,
  type EligibleEnrollmentPractitioner,
} from '@/lib/providerEnrollmentsService';
import type { Role } from '@/lib/rbacRoles';
import { ProviderEnrollmentsTab } from '@/pages/trainings/components';

export function EnrollmentsPage(): JSX.Element {
  const medplum = useMedplum();
  const { role } = usePermissions();
  const showPractitionerSelect = canSelectEnrollmentPractitioner(role);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const candidates = useQuery({
    queryKey: ['eligible-enrollment-practitioners', debouncedSearch],
    queryFn: () => fetchEligibleEnrollmentPractitioners(medplum, debouncedSearch),
    enabled: showPractitionerSelect,
  });
  // Prefix match: covers whichever practitioner the table is currently loading.
  const enrollmentsFetching = useIsFetching({ queryKey: ['provider-enrollments'] }) > 0;

  return (
    <EnrollmentsPageContent
      role={role}
      candidates={candidates}
      enrollmentsFetching={enrollmentsFetching}
      selectedPractitionerId={selectedPractitionerId}
      onSelectedPractitionerChange={setSelectedPractitionerId}
      search={search}
      onSearchChange={setSearch}
    />
  );
}

export function EnrollmentsPageContent({
  role,
  candidates,
  enrollmentsFetching,
  selectedPractitionerId,
  onSelectedPractitionerChange,
  search,
  onSearchChange,
}: {
  role: Role;
  candidates: {
    data?: EligibleEnrollmentPractitioner[];
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  enrollmentsFetching: boolean;
  selectedPractitionerId: string | null;
  onSelectedPractitionerChange: (value: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}): JSX.Element {
  const showPractitionerSelect = canSelectEnrollmentPractitioner(role);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group gap="xs" align="center">
          <div>
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={2}>Enrollments</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text c="dimmed" size="sm">
              Payer enrollment status
            </Text>
          </div>
        </Group>

        {showPractitionerSelect ? (
          <Stack gap="md">
            <Paper withBorder p="md">
              <Select
                label="Practitioner"
                placeholder="Select a practitioner"
                data={enrollmentPractitionerOptions(candidates.data ?? [])}
                value={selectedPractitionerId}
                onChange={onSelectedPractitionerChange}
                searchable
                searchValue={search}
                onSearchChange={onSearchChange}
                disabled={candidates.isLoading || enrollmentsFetching}
                rightSection={candidates.isLoading ? <Loader size="xs" /> : undefined}
                nothingFoundMessage="No practitioners found"
                clearable
              />
            </Paper>
            {candidates.isError ? (
              <Alert color="red" title="Unable to load practitioners">
                {candidates.error instanceof Error
                  ? candidates.error.message
                  : 'Practitioner options could not be loaded.'}
              </Alert>
            ) : selectedPractitionerId ? (
              <ProviderEnrollmentsTab practitionerId={selectedPractitionerId} />
            ) : (
              <Text c="dimmed" size="sm">
                Select a practitioner to view enrollment records.
              </Text>
            )}
          </Stack>
        ) : (
          <ProviderEnrollmentsTab />
        )}
      </Stack>
    </Container>
  );
}
